import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useMemo, useState } from "react";
import { Snackbar, Slide, Box, Typography, IconButton } from "@mui/material";
import { ToastContext } from "./ToastContext";
import { registerToastListener } from "./ToastUtils";
import { CloseOutlined } from "@mui/icons-material";
const DEFAULT_DURATION_MS = 5000;
const ICONS = {
    success: "/assets/toastIcons/right-tick.svg",
    warning: "/assets/toastIcons/warning-icon.svg",
    error: "/assets/toastIcons/cross-icon.svg",
};
const STYLES = {
    success: { bg: "#E8FAF0", text: "#17C964" },
    warning: { bg: "#FEFCE8", text: "#F5A524" },
    error: { bg: "#FEE7EF", text: "#B42318" },
    info: { bg: "#EEF2FF", text: "#4F46E5" },
};
function SlideFromRight(props) {
    return _jsx(Slide, { ...props, direction: "left" });
}
function normalizeSeverity(severity) {
    const s = (severity || "info").toLowerCase();
    if (s === "success" || s === "error" || s === "warning" || s === "info")
        return s;
    return "info";
}
export default function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const removeToast = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);
    const showToast = useCallback((message, options = {}) => {
        const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        const toast = {
            id,
            message: message ?? "",
            severity: normalizeSeverity(options.severity),
            duration: typeof options.duration === "number" ? options.duration : DEFAULT_DURATION_MS,
        };
        setToasts((prev) => [toast, ...prev].slice(0, 5));
        return id;
    }, []);
    React.useEffect(() => {
        const cleanup = registerToastListener((message, type) => {
            showToast(message, { severity: type });
        });
        return cleanup;
    }, [showToast]);
    const api = useMemo(() => {
        return {
            show: showToast,
            success: (msg, opts) => showToast(msg, { ...(opts || {}), severity: "success" }),
            error: (msg, opts) => showToast(msg, { ...(opts || {}), severity: "error" }),
            warning: (msg, opts) => showToast(msg, { ...(opts || {}), severity: "warning" }),
            info: (msg, opts) => showToast(msg, { ...(opts || {}), severity: "info" }),
            close: removeToast,
        };
    }, [removeToast, showToast]);
    return (_jsxs(ToastContext.Provider, { value: api, children: [children, _jsx(Box, { sx: {
                    position: "fixed",
                    top: 16,
                    right: 16,
                    zIndex: (theme) => theme.zIndex.snackbar,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    pointerEvents: "none",
                }, children: toasts.map((t) => (_jsx(Snackbar, { open: true, anchorOrigin: { vertical: "top", horizontal: "right" }, TransitionComponent: SlideFromRight, autoHideDuration: t.duration, onClose: (_, reason) => {
                        if (reason === "clickaway")
                            return;
                        removeToast(t.id);
                    }, sx: { position: "static", pointerEvents: "auto" }, children: _jsxs(Box, { sx: {
                            backgroundColor: (STYLES[t.severity] || STYLES.info).bg,
                            borderRadius: "16px",
                            padding: "12px",
                            display: "flex",
                            alignItems: "flex-start", // ✅ Changed to flex-start for multiline
                            gap: "16px",
                            minWidth: 280,
                            maxWidth: 420,
                        }, children: [_jsx(Box, { component: "img", alt: "", src: ICONS[t.severity] || ICONS.success, sx: { width: 24, height: 24, flex: "0 0 24px", marginTop: "2px" } }), _jsx(Typography, { sx: {
                                    fontFamily: "Inter, sans-serif",
                                    fontWeight: 500,
                                    fontSize: "14px",
                                    lineHeight: "20px",
                                    color: (STYLES[t.severity] || STYLES.info).text,
                                    whiteSpace: "pre-line", // ✅ Supports \n
                                    wordBreak: "break-word", // ✅ Prevents overflow
                                    flex: 1,
                                }, children: t.message }), _jsx(IconButton, { size: "small", onClick: () => removeToast(t.id), sx: {
                                    color: "inherit",
                                    padding: 0,
                                    marginLeft: "auto",
                                    flexShrink: 0,
                                }, children: _jsx(CloseOutlined, { sx: { width: 24, height: 24, color: "#797979" } }) })] }) }, t.id))) })] }));
}
